<template>
  <div class="products-table-wrap" data-testid="products-table-view">
  <div class="products-table__toolbar">
    <label class="products-table__sort-by">
      <span class="products-table__sort-by-label">Sort by</span>
      <select
        class="products-table__sort-select"
        data-testid="products-table-sort-select"
        :value="sortSelectValue"
        @change="onSortSelect($event.target.value)"
      >
        <option value="">List order</option>
        <option
          v-for="field in sortFields"
          :key="field.key"
          :value="field.key"
          :data-testid="`products-table-sort-option-${field.key}`"
        >
          {{ field.label }}
        </option>
      </select>
    </label>
  </div>
  <table class="products-table" data-testid="products-table">
    <thead>
      <tr>
        <th
          v-for="col in sortableColumns"
          :key="col.key"
          :class="`products-table__col-${col.key}`"
          :data-testid="`products-table-header-${col.key}`"
          :aria-sort="ariaSortFor(col.key)"
        >
          <button
            type="button"
            class="products-table__sort-btn"
            :data-testid="`products-table-sort-${col.key}`"
            :aria-label="sortAriaLabel(col)"
            @click="toggleSort(col.key)"
          >
            {{ col.label }}
            <span
              v-if="sortKey === col.key"
              class="material-icons products-table__sort-icon"
              :data-testid="`products-table-sort-${col.key}-icon`"
            >
              {{ sortDirection === "asc" ? "arrow_upward" : "arrow_downward" }}
            </span>
          </button>
        </th>
        <th class="products-table__col-actions" />
      </tr>
    </thead>
    <tbody>
      <!--
        Whole-row click opens the editor (Bug Intake #311). Interactive
        children (category link, visibility toggle, readiness flag, Edit)
        stop propagation so they keep their own action. The row is
        focusable and Enter (on the row itself, not a child button) opens
        the editor; the Edit button remains the explicit control.
      -->
      <tr
        v-for="(row, rowIndex) in sortedRows"
        :key="row.rowKey"
        class="products-table__row"
        :class="{
          'products-table__row--alt': rowIndex % 2 === 1,
          'products-table__row--disabled': campaign && campaign.complete,
        }"
        tabindex="0"
        :data-testid="`products-table-row-${row.rowKey}`"
        @click="editProduct(row)"
        @keydown.enter.self="editProduct(row)"
      >
        <td
          class="products-table__col-sku"
          :data-testid="`products-table-cell-sku-${row.rowKey}`"
        >
          <span v-if="row.sku">#{{ row.sku }}</span>
          <span v-else class="products-table__cell-empty">&mdash;</span>
        </td>
        <td
          class="products-table__col-name"
          :data-testid="`products-table-cell-name-${row.rowKey}`"
        >
          {{ row.name || "Untitled product" }}
        </td>
        <!--
          Reviewer P1 (gate round 2, PR #2571): parity with ProductCard.vue's
          incomplete-warning icon + product-readiness-flag. Both read from
          the SAME props StoreProducts/index.vue already computes for the
          cards (incompleteProductList, readinessByCampaignProductId) -- this
          component does not re-derive readiness/completeness itself, so the
          two views cannot diverge.
        -->
        <td
          class="products-table__col-status"
          :data-testid="`products-table-cell-status-${row.rowKey}`"
        >
          <span
            v-if="row.incomplete"
            class="products-table__incomplete-icon material-icons"
            :data-testid="`products-table-incomplete-${row.rowKey}`"
            :title="incompleteTooltipText"
            aria-label="Product contains incomplete information"
          >
            error
          </span>
          <uncovered-colors-marker
            :count="row.readiness ? row.readiness.uncovered_color_count : 0"
            :testid="`products-table-uncovered-colors-${row.rowKey}`"
          />
          <invalid-placement-marker
            :invalid="Boolean(row.readiness && row.readiness.invalid_placement)"
            :testid="`products-table-invalid-placement-${row.rowKey}`"
          />
          <dpi-status-icon
            :severity="row.readiness ? row.readiness.dpi_status : null"
            testid="product-dpi-icon"
          />
          <span v-if="row.readiness" @click.stop>
            <product-readiness-flag
              :state="row.readiness.state"
              :count="row.readiness.count"
              :decoration-count="row.readiness.decoration_count"
              :campaign-product-id="row.readinessId"
              @click="onReadinessClick"
            />
          </span>
        </td>
        <td
          class="products-table__col-supplier"
          :data-testid="`products-table-cell-supplier-${row.rowKey}`"
        >
          <span v-if="row.supplier">{{ row.supplier }}</span>
          <span
            v-else
            class="products-table__cell-empty"
            title="No supplier set"
            :data-testid="`products-table-supplier-unknown-${row.rowKey}`"
            >&mdash;</span
          >
        </td>
        <td
          class="products-table__col-price"
          :data-testid="`products-table-cell-price-${row.rowKey}`"
        >
          <span v-if="row.priceLabel != null">{{ row.priceLabel }}</span>
          <span
            v-else
            class="products-table__cell-empty"
            title="No price quoted yet"
            :data-testid="`products-table-price-unavailable-${row.rowKey}`"
            >&mdash;</span
          >
          <pricing-mode-badge
            :mode="row.pricingMode"
            :testid="`products-table-pricing-mode-${row.rowKey}`"
            class="products-table__pricing-mode"
          />
          <unpriced-sizes-marker
            :sizes="row.unpricedSizes"
            :testid="`products-table-unpriced-sizes-${row.rowKey}`"
            class="products-table__pricing-mode"
          />
        </td>
        <!--
          Category pill. A product has AT MOST ONE category: the single
          write path (PATCH /api/campaign_products/<id>/category) DELETEs
          any existing membership row before INSERTing, so there is no
          multi-category case to collapse or truncate here.

          Categorised -> a router-link pill to the Categories section,
          carrying ?category=<id> so that page can expand + scroll to it.
          Uncategorised -> an inert grey "Uncategorised" pill reusing the
          Categories page vocabulary (CategoryCard.vue's adopted pill),
          NOT a blank cell and NOT a link (there is nothing to open).
        -->
        <td
          class="products-table__col-category"
          :data-testid="`products-table-cell-category-${row.rowKey}`"
        >
          <router-link
            v-if="row.categoryId != null"
            class="products-table__category-pill products-table__category-link"
            :data-testid="`products-table-category-pill-${row.rowKey}`"
            @click.stop
            :to="categoryLinkFor(row)"
            :title="`Open ${row.categoryName} in the category editor`"
          >
            {{ row.categoryName }}
          </router-link>
          <span
            v-else
            class="products-table__category-pill products-table__category-pill--uncategorised"
            :data-testid="`products-table-category-uncategorised-${row.rowKey}`"
            title="Uncategorised - shown on the home page until you assign a category"
          >
            Uncategorised
          </span>
        </td>
        <!--
          Static status pill (Bug Intake #311, Jake 2026-10-26). It used to
          be a toggle button; it now only REPORTS the state and looks inert
          (no pointer cursor, no hover, no title). Visibility is changed on
          the product card and in the product editor. A click on it falls
          through to the whole-row "open editor" action like any other cell.
        -->
        <td
          class="products-table__col-visibility"
          :data-testid="`products-table-cell-visibility-${row.rowKey}`"
        >
          <span
            class="products-table__visibility-pill"
            :class="
              row.hidden
                ? 'products-table__visibility-pill--hidden'
                : 'products-table__visibility-pill--visible'
            "
            :data-testid="`products-table-visibility-pill-${row.rowKey}`"
          >
            {{ row.hidden ? "Hidden" : "Visible" }}
          </span>
        </td>
        <td class="products-table__col-actions">
          <button
            type="button"
            class="products-table__edit-btn"
            :data-testid="`products-table-edit-${row.rowKey}`"
            :disabled="campaign && campaign.complete"
            @click.stop="editProduct(row)"
          >
            Edit
          </button>
        </td>
      </tr>
    </tbody>
  </table>
  </div>
</template>

<script>
// ProductsTableView.vue -- tabular/list alternative to the ProductCard grid
// on the store-editor Products page (Boulders "Products page: tabular
// view", https://trello.com/c/R0K6f6oC). Toggled beside the existing card
// view (StoreProducts/index.vue owns the toggle); this component only
// renders rows for whatever `campaignProducts` it is handed.
//
// Inline price editing remains explicitly out of scope (separate P1 card,
// "Products page: inline pricing edit").
//
// This view is read-only: the Storefront column is a STATIC status pill
// (Jake 2026-10-26, Bug Intake #311; it was a toggle button from
// 2026-08-26). Visibility is written from the product card and the
// product editor, never from here. The only action is opening the editor.
//
// Field sourcing mirrors ProductCard.vue exactly, since this is a second
// view onto the SAME campaign_products data, not a new data source:
//   - Supplier: supplierLabel(campaignProduct) -- the vendor name via
//     supplier_id, else the persisted supplier_name, else a placeholder.
//     The SAME helper ProductCard.vue reads (supplierLabel.js).
//   - SKU: campaignProduct.product_item_number (the SanMar STYLE#; see
//     ProductCard.vue's `baseSku` computed). There is no `sku` column on
//     the product table.
//   - Price: the server's quote -- the lowest size price in the row's
//     `price_preview` ({size: cents}), or a neutral placeholder when the
//     row carries none. Never default_sale_price (2026-09-29).
//   - Pricing mode badge: campaignProduct.pricing_mode.
//   - Visibility: campaignProduct.hide_from_storefront (same field
//     ProductCard's `hideFromStorefront` computed reads).
//   - Last Edit: campaignProduct.updated_at (ISO-8601 from the product-list
//     endpoints; NULL/absent sorts last). Sort-only: not a rendered column.
//   - Category: campaignProduct.store_category_name / .store_category_id,
//     the category ENTITY membership. Both product-list endpoints
//     (getCampaignProductsLazy and getCampaignProductList) carry these
//     via the shared _row_to_product_dict, so the pill renders the same
//     regardless of which loader ran. NULL = uncategorised.
//   - Edit action: pushes to the same `product_editor` named route with
//     `campaignProductId`, matching ProductCard.vue's `editProduct()`.
import ProductReadinessFlag from "../readiness/ProductReadinessFlag.vue";
import PricingModeBadge from "./PricingModeBadge.vue";
import UnpricedSizesMarker from "./UnpricedSizesMarker.vue";
import UncoveredColorsMarker from "./UncoveredColorsMarker.vue";
import InvalidPlacementMarker from "./InvalidPlacementMarker.vue";
import DpiStatusIcon from "@/components/decoration/DpiStatusIcon.vue";
import { fromPriceCents, fromPriceLabel, unpricedFixedSizes } from "./pricingMode";
import { supplierLabel } from "./supplierLabel";

// The server's quote in dollars, or null. Deliberately NO fallback to
// default_sale_price: that is the old `basis * (1 + default_margin)`
// formula, which disagreed with the charge.
function rowPriceDollars(cp) {
  const cents = cp ? fromPriceCents(cp.price_preview) : null;
  return cents === null ? null : cents / 100;
}

// ISO-8601 -> epoch ms; null when absent or unparsable so the row sorts
// last rather than as the epoch.
function parseInstant(value) {
  if (typeof value !== "string" || value.length === 0) return null;
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}

export default {
  name: "ProductsTableView",

  components: {
    "product-readiness-flag": ProductReadinessFlag,
    "pricing-mode-badge": PricingModeBadge,
    "unpriced-sizes-marker": UnpricedSizesMarker,
    "uncovered-colors-marker": UncoveredColorsMarker,
    "invalid-placement-marker": InvalidPlacementMarker,
    "dpi-status-icon": DpiStatusIcon,
  },

  props: {
    campaign: {
      type: Object,
      required: false,
      default: null,
    },
    campaignProducts: {
      type: Array,
      required: false,
      default: () => [],
    },
    // Reviewer P1 (gate round 2, PR #2571): readiness/incomplete parity
    // with ProductCard.vue. These are the SAME props StoreProducts/
    // index.vue already computes for the card grid
    // (readinessByCampaignProductId, incompleteProductList) -- passed
    // straight through, never re-derived here, so the two views cannot
    // diverge on what counts as "incomplete" or what a product's
    // decoration-approval state is.
    readinessByCampaignProductId: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    // Index-aligned to campaignProducts, exactly like the card grid's
    // `:productIncomplete="incompleteProductList[index]"`.
    incompleteProductList: {
      type: Array,
      required: false,
      default: () => [],
    },
  },

  emits: ["readiness-click"],

  data() {
    return {
      // null = unsorted (server/list order); otherwise the key of the ONE
      // column currently sorted. Generalized from the original SKU-only
      // `skuSortDirection` -- same cycle semantics, now keyed by column.
      sortKey: null,
      // "asc" | "desc". Clicking the ACTIVE column flips direction;
      // clicking a DIFFERENT column starts that column at "asc" rather
      // than inheriting the previous column's direction (a descending
      // Price does not imply the user wants a descending Name). Never
      // returns to unsorted -- once a user asks to sort, toggling flips
      // direction rather than round-tripping through "no sort", matching
      // the behaviour the SKU-only implementation established.
      sortDirection: "asc",
    };
  },

  computed: {
    // ProductCard.vue's productIncompleteText computed branches on a Vuex
    // getter (getSmartShippingEnabled) + the specific campaignProduct's
    // product_weight to pick between two message strings. index.vue's
    // incompleteProductList is boolean-only (it does not carry the reason
    // text), and re-deriving that branch here would mean re-implementing
    // completeness logic in a second place -- exactly what the reviewer
    // asked NOT to do. The generic message covers the same TRUTH the
    // boolean encodes; only the differentiated wording is not
    // reproduced.
    incompleteTooltipText() {
      return "Product contains incomplete information";
    },

    // Column registry -- the single source for BOTH the rendered headers
    // and the comparator dispatch, so a column can never be sortable in
    // the header but unsorted in the body (or vice versa).
    //
    // `type` picks the comparator: "text" is locale+numeric-aware (the
    // behaviour the SKU column already had, preserved verbatim so
    // alphanumeric SanMar style numbers keep sorting as before),
    // "number" is numeric, "bool" groups false-before-true ascending.
    // Store id for the category pill's route target. Prefers _jv.id
    // (jsonapi-vuex canonical) and falls back to the bare column,
    // mirroring rowKeyOf's precedence for products.
    storeId() {
      const c = this.campaign;
      if (!c) return null;
      if (c._jv && c._jv.id != null) return c._jv.id;
      return c.id != null ? c.id : null;
    },

    sortableColumns() {
      return [
        // SKU leads (Bug Intake #311): the field builders scan for when
        // locating a specific product. The body <td> order in the
        // template must match this list.
        { key: "sku", label: "SKU", type: "text" },
        { key: "name", label: "Product", type: "text" },
        { key: "status", label: "Status", type: "number" },
        { key: "supplier", label: "Supplier", type: "text" },
        { key: "price", label: "Price", type: "number" },
        { key: "category", label: "Category", type: "text" },
        { key: "visibility", label: "Storefront", type: "bool" },
      ];
    },

    // Everything the "Sort by" dropdown offers: Last Edit first (the
    // common "what did I just touch" ask), then every sortable column.
    // Last Edit has no column of its own, so it is not in
    // sortableColumns (which drives the headers).
    sortFields() {
      return [
        { key: "last_edit", label: "Last Edit", type: "number", defaultDirection: "desc" },
        ...this.sortableColumns,
      ];
    },

    sortSelectValue() {
      return this.sortKey || "";
    },

    // Normalize each campaignProduct into the flat shape the template
    // reads, and resolve a stable rowKey (prefer _jv.id, mirroring the
    // `_cpId` convention in StoreProducts/index.vue).
    rows() {
      return (this.campaignProducts || []).map((cp, index) => {
        const rowKey = this.rowKeyOf(cp);
        // readinessByCampaignProductId is keyed by campaignProduct.id (see
        // StoreProducts/index.vue's ProductCard binding:
        // `readinessByCampaignProductId[campaignProduct.id] || null`) --
        // NOT rowKey, which prefers _jv.id. Mirror that exact lookup so a
        // product whose _jv.id and .id diverge still resolves readiness
        // the same way the card does.
        const readinessId = cp && cp.id;
        const readiness =
          (readinessId != null &&
            this.readinessByCampaignProductId &&
            this.readinessByCampaignProductId[readinessId]) ||
          null;
        return {
          rowKey,
          // ProductCard.vue passes `:campaign-product-id="campaignProduct.id"`
          // to ProductReadinessFlag (the SAME .id used for the
          // readinessByCampaignProductId lookup above) -- not _jv.id. Kept
          // as a separate field from rowKey (which prefers _jv.id) so the
          // flag's identity and the readiness lookup never disagree, even
          // when a row's .id and ._jv.id diverge.
          readinessId,
          name: cp && cp.product_name,
          supplier: supplierLabel(cp),
          sku:
            cp && typeof cp.product_item_number === "string" && cp.product_item_number.length > 0
              ? cp.product_item_number
              : null,
          // The server's quote (lowest size price in `price_preview`,
          // integer cents), or null. No client-side price math.
          price: rowPriceDollars(cp),
          // Display label: the SAME rule as the product card ("From $X"
          // when sizes differ), so card and table never disagree.
          priceLabel: cp ? fromPriceLabel(cp.price_preview) : null,
          // Fixed-price sizes with no price (#3884 gate P2-2).
          unpricedSizes: cp
            ? unpricedFixedSizes(cp.pricing_mode, cp.selected_sizes, cp.price_preview)
            : [],
          pricingMode: cp ? cp.pricing_mode : null,
          hidden: Boolean(cp && cp.hide_from_storefront),
          // Epoch ms of the last edit, or null (never edited / unparsable).
          // Compared as an instant so mixed UTC offsets order correctly.
          lastEdit: parseInstant(cp && cp.updated_at),
          // Category ENTITY membership, served by the SAME product-list
          // endpoints that already feed this table
          // (_row_to_product_dict in api/src/endpoints/product.py). NULL
          // for an uncategorised product -- rendered as the
          // "Uncategorised" pill rather than a blank cell, matching the
          // Categories page, where the Home card adopts exactly these
          // products and marks them with the same word (PR #3004).
          categoryId:
            cp && cp.store_category_id != null ? cp.store_category_id : null,
          categoryName:
            cp && typeof cp.store_category_name === "string" && cp.store_category_name.length > 0
              ? cp.store_category_name
              : null,
          incomplete: Boolean(this.incompleteProductList[index]),
          readiness,
          campaignProduct: cp,
        };
      });
    },

    sortedRows() {
      if (!this.sortKey) return this.rows;
      const column = this.sortFields.find((c) => c.key === this.sortKey);
      if (!column) return this.rows;
      const dir = this.sortDirection === "asc" ? 1 : -1;
      // Stable sort with an explicit tiebreak: Array.prototype.sort is
      // stable in modern JS engines, but we tiebreak on rowKey anyway so
      // the order is deterministic even if source order changes between
      // renders (e.g. a reload reordering equal-valued rows differently).
      return [...this.rows].sort((a, b) => {
        const av = this.sortValue(a, column);
        const bv = this.sortValue(b, column);
        // MISSING VALUES SORT LAST IN BOTH DIRECTIONS -- deliberately
        // NOT multiplied by `dir`. "No value" is not "greatest value":
        // flipping to descending should reverse the products that HAVE
        // a value, not float the blanks to the top where they push real
        // data off the first screen.
        if (av == null && bv == null) return this.compareKey(a, b);
        if (av == null) return 1;
        if (bv == null) return -1;
        const cmp = this.compareValues(av, bv, column.type);
        if (cmp !== 0) return cmp * dir;
        return this.compareKey(a, b);
      });
    },
  },

  methods: {
    rowKeyOf(cp) {
      if (!cp) return null;
      if (cp._jv && cp._jv.id != null) {
        const n = Number(cp._jv.id);
        return Number.isFinite(n) ? n : cp._jv.id;
      }
      if (cp.id != null) {
        const n = Number(cp.id);
        return Number.isFinite(n) ? n : cp.id;
      }
      return null;
    },

    // Ascending-by-id tiebreak, stable regardless of sort direction --
    // ties break the same way whether sorting SKU asc or desc.
    compareKey(a, b) {
      const ak = a.rowKey;
      const bk = b.rowKey;
      if (ak == null && bk == null) return 0;
      if (ak == null) return 1;
      if (bk == null) return -1;
      if (ak < bk) return -1;
      if (ak > bk) return 1;
      return 0;
    },

    // Per-column sort value. Returning null means "missing" and routes
    // into the sorts-last branch in sortedRows.
    sortValue(row, column) {
      switch (column.key) {
        case "name":
          return row.name || null;
        case "supplier":
          return row.supplier;
        case "sku":
          return row.sku;
        case "price": {
          const n = Number(row.price);
          return Number.isFinite(n) ? n : null;
        }
        case "category":
          return row.categoryName;
        case "last_edit":
          return row.lastEdit;
        case "visibility":
          // false (visible) sorts before true (hidden) ascending.
          return row.hidden;
        case "status":
          // Status is a composite cell (an incomplete warning icon plus a
          // readiness flag), so there is no single displayed string to
          // sort on. Rank by URGENCY -- incomplete products first, then
          // products carrying a readiness state, then clean rows -- which
          // is the order a builder wants to work the list in. Rows with
          // nothing to report share rank 0 and fall through to the
          // rowKey tiebreak, so the sort stays deterministic.
          return (row.incomplete ? 2 : 0) + (row.readiness ? 1 : 0);
        default:
          return null;
      }
    },

    compareValues(a, b, type) {
      if (type === "number") {
        // Numeric, NOT lexical: "9.00" must sort before "100.00".
        return a === b ? 0 : a < b ? -1 : 1;
      }
      if (type === "bool") {
        const an = a ? 1 : 0;
        const bn = b ? 1 : 0;
        return an === bn ? 0 : an < bn ? -1 : 1;
      }
      // Locale + numeric-aware string compare -- preserves the exact
      // behaviour the SKU column already had for alphanumeric SanMar
      // style numbers ("9000" before "18000") and case-insensitive
      // ordering, now applied to every text column.
      return String(a).localeCompare(String(b), undefined, { numeric: true });
    },

    ariaSortFor(key) {
      if (this.sortKey !== key) return "none";
      return this.sortDirection === "asc" ? "ascending" : "descending";
    },

    sortAriaLabel(column) {
      if (this.sortKey !== column.key) return `Sort by ${column.label}`;
      return this.sortDirection === "asc"
        ? `Sort by ${column.label} descending`
        : `Sort by ${column.label} ascending`;
    },

    toggleSort(key) {
      if (this.sortKey === key) {
        // Clicking the ACTIVE column flips direction (asc <-> desc,
        // never back to unsorted) -- the cycle the SKU-only version
        // established.
        this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
        return;
      }
      // A DIFFERENT column always starts ascending rather than inheriting
      // the previous column's direction.
      this.sortKey = key;
      this.sortDirection = "asc";
    },

    // "Sort by" dropdown. Same state the header buttons drive, so the two
    // controls cannot disagree. A field starts in its natural direction
    // (Last Edit newest-first, everything else ascending); the header
    // button still flips it. The blank option returns to list order.
    onSortSelect(key) {
      if (!key) {
        this.sortKey = null;
        this.sortDirection = "asc";
        return;
      }
      const field = this.sortFields.find((f) => f.key === key);
      if (!field) return;
      this.sortKey = key;
      this.sortDirection = field.defaultDirection || "asc";
    },

    // Route target for the category pill. Carries the store id in the
    // PATH (so a direct visit / refresh resolves the right store without
    // any prior in-app navigation) and the category id as a QUERY param,
    // which StoreCategories/index.vue reads to expand + scroll to it.
    categoryLinkFor(row) {
      return {
        path: `/distributor/stores/${this.storeId}/edit/categories`,
        query: { category: String(row.categoryId) },
      };
    },

    editProduct(row) {
      if (row.rowKey == null) return;
      // Same guard as the disabled Edit button, so the whole-row click
      // cannot open a completed campaign's product.
      if (this.campaign && this.campaign.complete) return;
      this.$router.push({
        name: "product_editor",
        params: { campaignProductId: String(row.rowKey) },
      });
    },

    // Forward the flag's click payload unchanged. index.vue wires this to
    // the SAME onCardReadinessClick handler the card grid uses (pure
    // navigation, no card-specific state), so clicking the flag in either
    // view does the identical thing: push to the Approval Workspace
    // filtered to this product.
    onReadinessClick(payload) {
      this.$emit("readiness-click", payload);
    },
  },
};
</script>

<style scoped>
.products-table__pricing-mode {
  margin-left: 8px;
}

.products-table {
  width: 100%;
  border-collapse: collapse;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  overflow: hidden;
  font-family: "Roboto", sans-serif;
  font-size: 13.5px;
}

.products-table thead th {
  text-align: left;
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  color: var(--tf-fg-4, rgba(0, 0, 0, 0.55));
  padding: 10px 14px;
  border-bottom: 1px solid #e0e0e0;
  background: #fafbfc;
}

.products-table tbody tr {
  border-bottom: 1px solid #e3e6ea;
}

/* Row banding + hover/focus (Bug Intake #311). Whole row opens the
   editor, so the row reads as a single clickable target. */
.products-table tbody tr.products-table__row--alt {
  background: #f6f8fa;
}

.products-table tbody tr.products-table__row {
  cursor: pointer;
}

.products-table tbody tr.products-table__row--disabled {
  cursor: default;
}

.products-table tbody tr.products-table__row:hover {
  background: #e8f1fb;
}

.products-table tbody tr.products-table__row:focus-visible {
  outline: 2px solid var(--tf-primary, #1976d2);
  outline-offset: -2px;
}

.products-table tbody tr:last-child {
  border-bottom: none;
}

.products-table tbody td {
  padding: 12px 14px;
  color: #212121;
  vertical-align: middle;
}

.products-table__col-sku {
  font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
  color: var(--tf-fg-3, rgba(0, 0, 0, 0.7));
}

.products-table__col-status {
  white-space: nowrap;
}

.products-table__incomplete-icon {
  color: #d32f2f;
  font-size: 20px;
  vertical-align: middle;
  margin-right: 6px;
  cursor: help;
}

.products-table__sort-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  font-weight: 600;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  color: inherit;
  cursor: pointer;
}

.products-table__sort-icon {
  font-size: 14px;
}

.products-table__cell-empty {
  color: var(--tf-fg-4, rgba(0, 0, 0, 0.4));
}

/* Static status pill: deliberately NOT styled as a control (no pointer
   cursor, no hover, no focus ring). Bug Intake #311. */
.products-table__visibility-pill {
  display: inline-flex;
  align-items: center;
  font-size: 11.5px;
  font-weight: 500;
  padding: 3px 9px;
  border-radius: 9999px;
  white-space: nowrap;
  cursor: default;
}

/* Category pill. The linked variant reuses the count-chip blue from
   CategoryCard.vue so a category reads the same colour on both surfaces;
   the uncategorised variant reuses that page's neutral grey adopted-pill
   treatment -- a normal state a builder may simply not have gotten to
   yet, not a warning. */
.products-table__category-pill {
  display: inline-flex;
  align-items: center;
  font-size: 11.5px;
  font-weight: 500;
  padding: 3px 9px;
  border-radius: 9999px;
  white-space: nowrap;
  text-decoration: none;
  color: #0a6cb0;
  background: var(--tf-primary-soft, #e3f2fd);
}

a.products-table__category-pill:hover,
a.products-table__category-pill:focus-visible {
  text-decoration: underline;
}

/* Category is NOT inline-editable (the row opens the editor; the pill is
   only a link to the Categories page). Drop the filled-chip look so it
   does not read as an editable control (Bug Intake #311). */
.products-table__category-link {
  background: none;
  padding: 0;
  border-radius: 0;
  text-decoration: underline;
  text-decoration-color: transparent;
}

a.products-table__category-link:hover,
a.products-table__category-link:focus-visible {
  text-decoration-color: currentColor;
}

.products-table__category-pill--uncategorised {
  background: none;
  padding: 0;
  border-radius: 0;
  font-weight: 400;
  color: var(--tf-fg-3, #5f6368);
}

.products-table__visibility-pill--visible {
  background: #e6f7ed;
  color: #12805a;
}

.products-table__visibility-pill--hidden {
  background: #f5f5f5;
  color: var(--tf-fg-4, rgba(0, 0, 0, 0.5));
}

.products-table__toolbar {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 8px;
}

.products-table__sort-by {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12.5px;
  color: var(--tf-fg-3, rgba(0, 0, 0, 0.7));
}

.products-table__sort-select {
  font: inherit;
  padding: 5px 10px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  background: #fff;
  color: #212121;
}

.products-table__edit-btn {
  background: none;
  border: 1px solid #cbd5e1;
  border-radius: 9999px;
  padding: 5px 14px;
  font-size: 12.5px;
  font-weight: 500;
  color: #212121;
  cursor: pointer;
}

.products-table__edit-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
